import React, { useState } from "react";
import { Box, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";

// Icons
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";

import { RectangularTextButton } from "components/Base/Buttons";

interface EntityEmptyPageItemBase {
  /** Unique key for React list rendering */
  id: string | number;
  /** Whether to show this item */
  visible?: boolean;
}

interface TextDisplayItem extends EntityEmptyPageItemBase {
  type: "text";
  /** Main label text, e.g. “Select a Campaign” */
  text: string;
  /** Direction of arrows shown beside the text */
  direction?: "left" | "right" | "up" | "down";
  /** Optional text variant, defaults to h5 */
  variant?: "body1" | "body2" | "h6" | "h5" | "h4";
}

interface ButtonItem extends EntityEmptyPageItemBase {
  type: "button";
  /** Label for the button */
  label: string;
  /** Function to run when clicked, optional if opens popup */
  onClick?: () => void;
  /** Whether this button should open the AddPopupComponent */
  openPopup?: boolean;
  /**
   * highlighted = filled route-colour button
   * clear = transparent/white outlined button
   */
  colour?: "highlighted" | "clear";
}

type EntityEmptyPageItem = TextDisplayItem | ButtonItem;

interface EntityEmptyPageProps {
  /** Optional top title */
  title?: string;

  /** Theme color key to use, e.g. “campaignsPrimary” */
  colorKey?: string;

  /** Optional popup component to render for creation */
  AddPopupComponent?: React.ComponentType<{
    open: boolean;
    onClose: () => void;
    onSaved?: (newEntity: any) => void;
  }>;

  /** Called when a new entity is created in popup */
  onEntityCreated?: (newEntity: any) => void;

  /** List of items to display */
  items?: EntityEmptyPageItem[];

  /** Default vertical gap between items */
  gap?: number;
}

/** Map direction to arrow icon */
const arrowMap = {
  left: ArrowBackIcon,
  right: ArrowForwardIcon,
  up: ArrowUpwardIcon,
  down: ArrowDownwardIcon,
};

const EntityEmptyPage: React.FC<EntityEmptyPageProps> = ({
  title,
  colorKey,
  AddPopupComponent,
  onEntityCreated,
  items = [],
  gap = 3,
}) => {
  const theme = useTheme();
  const [openDialog, setOpenDialog] = useState(false);

  const primaryColor =
    colorKey && (theme.custom as any)[colorKey]
      ? (theme.custom as any)[colorKey].primary.dark
      : theme.custom.base.primary.dark;

  const handleOpenPopup = () => setOpenDialog(true);
  const handleClosePopup = () => setOpenDialog(false);

  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="flex-start"
      height="100%"
      textAlign="center"
      gap={gap}
      mt={5}
    >
      {title && (
        <Typography variant="h2" color={primaryColor}>
          {title.toUpperCase()}
        </Typography>
      )}

      {items
        .filter((item) => item.visible !== false)
        .map((item) => {
          if (item.type === "text") {
            const Arrow = item.direction ? arrowMap[item.direction] : null;

            return (
              <Box
                key={item.id}
                display="flex"
                alignItems="center"
                justifyContent="center"
                gap={1}
              >
                {Arrow && (
                  <Arrow sx={{ color: theme.custom.base.text.primary.dark }} />
                )}

                <Typography
                  variant={item.variant ?? "h5"}
                  sx={{ color: theme.custom.base.text.primary.dark }}
                >
                  {item.text}
                </Typography>

                {Arrow && (
                  <Arrow sx={{ color: theme.custom.base.text.primary.dark }} />
                )}
              </Box>
            );
          }

          if (item.type === "button") {
            const handleClick = () => {
              if (item.openPopup) handleOpenPopup();
              else item.onClick?.();
            };

            return (
              <RectangularTextButton
                key={item.id}
                colour={item.colour ?? "highlighted"}
                colorKey={colorKey}
                primaryColor={primaryColor}
                textColor={theme.custom.base.text.primary.dark}
                buttonSize="medium"
                onClick={handleClick}
              >
                {item.label}
              </RectangularTextButton>
            );
          }

          return null;
        })}

      {AddPopupComponent && (
        <AddPopupComponent
          open={openDialog}
          onClose={handleClosePopup}
          onSaved={(newEntity) => {
            handleClosePopup();
            onEntityCreated?.(newEntity);
          }}
        />
      )}
    </Box>
  );
};

export default EntityEmptyPage;